import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { connectNotificationStream, type StreamHandlers } from '@/notifications/stream'
import { CARD_PATH, fetchTelephonyStatus, type IncomingCall } from './api'

/**
 * Всплывающая карточка входящего звонка (тикет #122).
 *
 * Сервер шлёт кадр `incoming-call` в тот же живой канал, что и уведомления (SSE через брокер,
 * #118: вкладка может быть открыта на любом узле). В кадре — карточка, найденная по номеру, и
 * её id: окно ведёт прямо на неё. Неизвестный номер называется словами — «новый номер» — и
 * предлагает завести лид с уже подставленным телефоном: пустое окно читалось бы как сбой поиска.
 *
 * Канал открывается, только когда телефония подключена: без АТС окну неоткуда взяться, а лишнее
 * соединение на каждую вкладку — нагрузка на сервер без пользы.
 *
 * Окно не исчезает само: менеджер может взять трубку и только потом посмотреть на экран, а
 * пропавшее окно не вернуть. Закрывается крестиком; тот же звонок второй раз не показывается
 * (сервер шлёт одно окно на человека, но переподключение канала может повторить кадр).
 */
export function IncomingCallPopup({
  connect = connectNotificationStream,
}: {
  /** Подмена канала в тестах; по умолчанию — живой поток уведомлений. */
  connect?: (handlers: StreamHandlers) => () => void
}) {
  const { t, user } = useApp()
  const status = useQuery({
    queryKey: ['telephony-status'],
    queryFn: ({ signal }) => fetchTelephonyStatus(signal),
    enabled: user != null,
    retry: false,
    staleTime: 5 * 60_000,
  })
  const [calls, setCalls] = useState<IncomingCall[]>([])
  const configured = status.data?.configured === true

  useEffect(() => {
    if (!configured) return undefined
    return connect({
      onNotification: () => {},
      onUnreadCount: () => {},
      onIncomingCall: (frame) => {
        const call = frame as unknown as IncomingCall
        if (call.callId == null) return
        setCalls((current) => (current.some((known) => known.callId === call.callId) ? current : [call, ...current]))
      },
    })
  }, [configured, connect])

  if (calls.length === 0) return null
  const dismiss = (callId: string) => setCalls((current) => current.filter((call) => call.callId !== callId))
  const mayCreateLead = can(user, 'sales.lead', 'create')

  return (
    <div className="incoming-calls" style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 60, display: 'grid', gap: 8, maxWidth: 360 }}>
      {calls.map((call) => {
        const match = call.match ?? null
        const path = match ? `${CARD_PATH[match.entityType]}/${match.entityId}` : null
        return (
          <section key={call.callId} className="panel stack" role="dialog" aria-label={t('telephony.incoming.title')} style={{ gap: 8, boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,.18))' }}>
            <div className="row" style={{ gap: 8, justifyContent: 'space-between' }}>
              <span className="row" style={{ gap: 6 }}>
                <Icon name="phone" />
                <strong>{t('telephony.incoming.title')}</strong>
              </span>
              <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('telephony.incoming.close')} onClick={() => dismiss(call.callId)}>
                <Icon name="close" size="sm" />
              </button>
            </div>
            <span className="mono">{call.number ?? t('telephony.incoming.hidden')}</span>
            {match ? (
              <>
                <span>
                  <span className="t-sm muted">{t(`telephony.entity.${match.entityType}`)} · </span>
                  {match.title ?? '—'}
                </span>
                {match.counterpartyName != null && match.entityType !== 'counterparty' && (
                  <span className="t-sm muted">{match.counterpartyName}</span>
                )}
                {(call.otherMatches ?? 0) > 0 && (
                  <span className="t-sm muted">{t('telephony.incoming.others', { count: call.otherMatches ?? 0 })}</span>
                )}
                {path && (
                  <Link className="btn btn-primary btn-sm" to={path} onClick={() => dismiss(call.callId)}>
                    {t('telephony.incoming.open')}
                  </Link>
                )}
              </>
            ) : (
              <>
                <span className="badge">{t('telephony.incoming.unknown')}</span>
                {mayCreateLead && (
                  <Link
                    className="btn btn-secondary btn-sm"
                    to={`/leads/new?phone=${encodeURIComponent(call.number ?? '')}`}
                    onClick={() => dismiss(call.callId)}
                  >
                    {t('telephony.incoming.createLead')}
                  </Link>
                )}
              </>
            )}
          </section>
        )
      })}
    </div>
  )
}
